import React, { useState, useEffect } from 'react';
import { Sidebar, MobileHeader } from '../components/shared/Sidebar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import axios from '../lib/axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  AlertTriangle, Trash2, Download, Upload, Shield, 
  Database, Users, FileText, Wallet, RefreshCw, X,
  CheckCircle, History, Lock
} from 'lucide-react';
import { formatDateTime } from '../lib/utils';

const THEME = {
  neonCyan: '#00F5FF',
  neonPurple: '#9945FF',
  neonGold: '#FFD700',
  neonGreen: '#00FF88',
  neonPink: '#FF3366',
  neonOrange: '#FF6B00',
};

// Confirmation Modal
function ConfirmationModal({ title, message, onConfirm, onCancel, requirePassword = false }) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    await onConfirm(password);
    setLoading(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl overflow-hidden"
        style={{ background: '#0d0d15', border: `2px solid ${THEME.neonPink}50` }}
      >
        <div className="p-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl" style={{ background: `${THEME.neonPink}20` }}>
              <AlertTriangle className="w-6 h-6" style={{ color: THEME.neonPink }} />
            </div>
            <h2 className="text-xl font-black text-white">{title}</h2>
          </div>
        </div>
        <div className="p-6">
          <p className="text-gray-300 mb-4">{message}</p>
          <div className="p-4 rounded-xl mb-4" style={{ background: `${THEME.neonPink}10`, border: `1px solid ${THEME.neonPink}30` }}>
            <p className="text-sm font-bold" style={{ color: THEME.neonPink }}>⚠️ This action cannot be undone!</p>
          </div>
          
          {requirePassword && (
            <div className="mb-4">
              <Label className="text-gray-400 text-sm">Confirm Admin Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pl-10 bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              onClick={handleConfirm}
              disabled={loading || (requirePassword && !password)}
              className="flex-1 font-bold"
              style={{ background: `linear-gradient(135deg, ${THEME.neonPink}, ${THEME.neonOrange})` }}
            >
              {loading ? 'Processing...' : 'Confirm Delete'}
            </Button>
            <Button
              variant="outline"
              onClick={onCancel}
              className="flex-1 border-white/10"
            >
              Cancel
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function SystemResetPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [resetOptions, setResetOptions] = useState({
    delete_players: false,
    delete_transactions: false,
    delete_wallets: false,
    delete_employees: false,
    reset_entire_system: false
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restorePassword, setRestorePassword] = useState('');

  useEffect(() => {
    fetchAuditLogs();
  }, []);

  const fetchAuditLogs = async () => {
    try {
      const res = await axios.get('/admin/audit-logs');
      setAuditLogs(res.data.logs || []);
    } catch (err) {
      console.error('Failed to fetch audit logs');
    }
  };

  const handleResetConfirm = async (password) => {
    try {
      const res = await axios.post('/admin/system-reset', {
        ...resetOptions,
        admin_password: password
      });
      toast.success('System reset completed');
      setShowConfirmation(false);
      setResetOptions({
        delete_players: false,
        delete_transactions: false,
        delete_wallets: false,
        delete_employees: false,
        reset_entire_system: false
      });
      fetchAuditLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    }
  };

  const handleBackup = async () => {
    setBackupLoading(true);
    try {
      const res = await axios.get('/admin/backup', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rider_backup_${new Date().toISOString().slice(0, 10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Backup downloaded successfully');
      fetchAuditLogs();
    } catch (err) {
      toast.error('Failed to download backup');
    }
    setBackupLoading(false);
  };

  const handleRestore = async () => {
    if (!restoreFile || !restorePassword) return;
    setRestoreLoading(true);
    try {
      const fileContent = await restoreFile.text();
      const backupData = JSON.parse(fileContent);
      
      await axios.post('/admin/restore', {
        backup_data: backupData,
        admin_password: restorePassword
      });
      
      toast.success('Database restored successfully');
      setShowRestoreModal(false);
      setRestoreFile(null);
      setRestorePassword('');
      fetchAuditLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    }
    setRestoreLoading(false);
  };

  const selectedCount = Object.values(resetOptions).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#080810]" data-testid="system-reset-page">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <MobileHeader onMenuClick={() => setSidebarOpen(true)} />

      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 right-1/4 w-[600px] h-[600px] bg-red-500/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 left-1/4 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-[150px]" />
      </div>

      <main className="lg:ml-72 min-h-screen relative z-10 p-4 md:p-6 lg:p-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl" style={{ background: `${THEME.neonPink}20`, border: `1px solid ${THEME.neonPink}30` }}>
              <Shield className="w-8 h-8" style={{ color: THEME.neonPink }} />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-black text-white">SYSTEM ADMIN</h1>
              <p className="text-gray-400">Reset, Backup & Restore</p>
            </div>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Backup & Restore */}
          <Card className="p-6 rounded-2xl bg-[#0d0d15] border-0">
            <div className="flex items-center gap-2 mb-6">
              <Database className="w-5 h-5" style={{ color: THEME.neonCyan }} />
              <h2 className="text-xl font-bold text-white">Database Backup</h2>
            </div>
            
            <div className="space-y-4">
              <p className="text-gray-400 text-sm">
                Download a complete backup of your database including players, transactions, wallets, employees, and chat data.
              </p>
              
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleBackup}
                disabled={backupLoading}
                className="w-full p-4 rounded-xl font-bold flex items-center justify-center gap-2 text-black"
                style={{ background: `linear-gradient(135deg, ${THEME.neonCyan}, ${THEME.neonGreen})` }}
              >
                {backupLoading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <Download className="w-5 h-5" />
                )}
                {backupLoading ? 'Generating Backup...' : 'Download Backup'}
              </motion.button>

              <div className="pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <p className="text-gray-400 text-sm mb-4">
                  Restore database from a previous backup file.
                </p>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowRestoreModal(true)}
                  className="w-full p-4 rounded-xl font-bold flex items-center justify-center gap-2 border border-white/20 text-white hover:bg-white/5"
                >
                  <Upload className="w-5 h-5" />
                  Restore from Backup
                </motion.button>
              </div>
            </div>
          </Card>

          {/* System Reset */}
          <Card className="p-6 rounded-2xl bg-[#0d0d15] border-0">
            <div className="flex items-center gap-2 mb-6">
              <Trash2 className="w-5 h-5" style={{ color: THEME.neonPink }} />
              <h2 className="text-xl font-bold text-white">System Reset</h2>
            </div>

            <div className="space-y-3 mb-6">
              {[
                { key: 'delete_players', label: 'Delete All Players', icon: Users, color: THEME.neonCyan },
                { key: 'delete_transactions', label: 'Delete All Transactions', icon: FileText, color: THEME.neonGreen },
                { key: 'delete_wallets', label: 'Delete All Wallets', icon: Wallet, color: THEME.neonGold },
                { key: 'delete_employees', label: 'Delete All Employees', icon: Users, color: THEME.neonPurple },
                { key: 'reset_entire_system', label: 'Reset Entire System', icon: AlertTriangle, color: THEME.neonPink }
              ].map(({ key, label, icon: Icon, color }) => (
                <button
                  key={key}
                  onClick={() => {
                    if (key === 'reset_entire_system') {
                      setResetOptions({
                        delete_players: true,
                        delete_transactions: true,
                        delete_wallets: true,
                        delete_employees: true,
                        reset_entire_system: true
                      });
                    } else {
                      setResetOptions(prev => ({ ...prev, [key]: !prev[key], reset_entire_system: false }));
                    }
                  }}
                  className={`w-full p-4 rounded-xl flex items-center gap-3 transition-all ${
                    resetOptions[key] ? 'ring-2' : ''
                  }`}
                  style={{ 
                    background: resetOptions[key] ? `${color}20` : 'rgba(255,255,255,0.03)',
                    borderColor: resetOptions[key] ? color : 'transparent',
                    ringColor: resetOptions[key] ? color : 'transparent'
                  }}
                >
                  <div className="p-2 rounded-lg" style={{ background: `${color}20` }}>
                    <Icon className="w-5 h-5" style={{ color }} />
                  </div>
                  <span className="text-white font-semibold">{label}</span>
                  {resetOptions[key] && (
                    <CheckCircle className="w-5 h-5 ml-auto" style={{ color }} />
                  )}
                </button>
              ))}
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowConfirmation(true)}
              disabled={selectedCount === 0}
              className="w-full p-4 rounded-xl font-bold flex items-center justify-center gap-2 text-white disabled:opacity-50"
              style={{ 
                background: selectedCount > 0 
                  ? `linear-gradient(135deg, ${THEME.neonPink}, ${THEME.neonOrange})` 
                  : 'rgba(255,255,255,0.1)'
              }}
            >
              <Trash2 className="w-5 h-5" />
              {selectedCount > 0 ? `Reset Selected (${selectedCount})` : 'Select options to reset'}
            </motion.button>
          </Card>
        </div>

        {/* Audit Logs */}
        <Card className="mt-6 rounded-2xl bg-[#0d0d15] border-0 overflow-hidden">
          <div className="p-4 flex items-center gap-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <History className="w-5 h-5" style={{ color: THEME.neonPurple }} />
            <h3 className="font-bold text-white">Audit Logs</h3>
            <Badge className="ml-2" style={{ background: `${THEME.neonPurple}20`, color: THEME.neonPurple }}>
              {auditLogs.length}
            </Badge>
          </div>
          <ScrollArea className="max-h-[300px]">
            <div className="p-4 space-y-2">
              {auditLogs.map((log, idx) => (
                <div key={log.id || idx} className="p-3 rounded-xl bg-white/5 flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-white/10">
                    {log.action === 'delete_player' || log.action === 'delete_employee' ? (
                      <Trash2 className="w-4 h-4 text-red-400" />
                    ) : log.action === 'database_backup' ? (
                      <Download className="w-4 h-4 text-cyan-400" />
                    ) : log.action === 'database_restore' ? (
                      <Upload className="w-4 h-4 text-green-400" />
                    ) : (
                      <RefreshCw className="w-4 h-4 text-orange-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-white capitalize">{log.action?.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-gray-500">by {log.performed_by_name} • {formatDateTime(log.timestamp)}</p>
                  </div>
                </div>
              ))}
              {auditLogs.length === 0 && (
                <p className="text-center text-gray-500 py-8">No audit logs yet</p>
              )}
            </div>
          </ScrollArea>
        </Card>
      </main>

      {/* Confirmation Modal */}
      <AnimatePresence>
        {showConfirmation && (
          <ConfirmationModal
            title="Confirm System Reset"
            message="You are about to permanently delete selected data. This action cannot be undone. Please enter your admin password to confirm."
            requirePassword={true}
            onConfirm={handleResetConfirm}
            onCancel={() => setShowConfirmation(false)}
          />
        )}
      </AnimatePresence>

      {/* Restore Modal */}
      <AnimatePresence>
        {showRestoreModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowRestoreModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={e => e.stopPropagation()}
              className="w-full max-w-md rounded-2xl overflow-hidden"
              style={{ background: '#0d0d15', border: `2px solid ${THEME.neonCyan}50` }}
            >
              <div className="p-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl" style={{ background: `${THEME.neonCyan}20` }}>
                      <Upload className="w-6 h-6" style={{ color: THEME.neonCyan }} />
                    </div>
                    <h2 className="text-xl font-black text-white">Restore Database</h2>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => setShowRestoreModal(false)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="p-4 rounded-xl" style={{ background: `${THEME.neonOrange}10`, border: `1px solid ${THEME.neonOrange}30` }}>
                  <p className="text-sm" style={{ color: THEME.neonOrange }}>
                    ⚠️ This will replace all current data with the backup. Make sure to download a current backup first.
                  </p>
                </div>

                <div>
                  <Label className="text-gray-400 text-sm">Backup File (JSON)</Label>
                  <input
                    type="file"
                    accept=".json"
                    onChange={e => setRestoreFile(e.target.files?.[0] || null)}
                    className="mt-2 w-full p-3 rounded-xl bg-white/5 border border-white/10 text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-purple-500 file:text-white file:font-semibold"
                  />
                </div>

                <div>
                  <Label className="text-gray-400 text-sm">Admin Password</Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      type="password"
                      value={restorePassword}
                      onChange={e => setRestorePassword(e.target.value)}
                      placeholder="Confirm password"
                      className="pl-10 bg-white/5 border-white/10 text-white"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleRestore}
                  disabled={restoreLoading || !restoreFile || !restorePassword}
                  className="w-full font-bold"
                  style={{ background: `linear-gradient(135deg, ${THEME.neonCyan}, ${THEME.neonGreen})` }}
                >
                  {restoreLoading ? 'Restoring...' : 'Restore Database'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
